import * as React from 'react';
import * as _ from 'underscore';

import BooleanField from './BooleanField';
import { ITreeItemGeneral } from '../ts/Interfaces';

export interface IToggleStates {
    [id: string]: boolean;
}

export interface ITreeItemGeneralProperties {
    arr: ITreeItemGeneral[];

    id?: string;
    name?: string;
    shadow?: boolean;
    select?: string;
    focusedItemID?: string;

    collapsed?: boolean;
    collapsedDict: IToggleStates;

    showCheckbox?: boolean;
    checked?: boolean;
    checkedItems?: IToggleStates;

    obj?: ITreeItemGeneral;

    displayFilter?(...args): boolean;
    nameFunc?(obj: ITreeItemGeneral, title: string): string | React.JSX.Element[];
    getCustomIcon?(obj: ITreeItemGeneral): React.JSX.Element;

    hiddenFilter?(...args): boolean; //LL: 10.09.19

    onSelect?(id: string): void;
    onDoubleClick?(id: string): void;
    onLoadLeafCall?(id: string): void;
    onCollapsedChange?(id: string, collapsed: boolean): void;
    onCheckedChange?(id: string, value: boolean): void;
}

function isCollapsed(collapsed: boolean, collapsedDict: IToggleStates, id: string) {
    if (collapsed != null) {
        return collapsed;
    }

    if (collapsedDict != null && collapsedDict[id] != null) {
        return collapsedDict[id];
    }

    return true;
}

function isChecked(checked: boolean, checkedItems: IToggleStates, id: string) {
    let result: boolean;

    if (checked != null) {
        result = checked;
    } else if (checkedItems != null && checkedItems[id] != null) {
        result = checkedItems[id];
    } else {
        result = false;
    }

    return result;
}

export default function TreeItemGeneral(props: ITreeItemGeneralProperties) {
    const [collapsed, setCollapsed] = React.useState(isCollapsed(props.collapsed, props.collapsedDict, props.id));
    const [checked, setChecked] = React.useState(isChecked(props.checked, props.checkedItems, props.id));

    const tryToLoadLeaf = React.useCallback(() => {
        if (props.obj?.expandable && props.arr.length === 0) {
            if (props.onLoadLeafCall != null) {
                props.onLoadLeafCall(props.id);
            }
        }
    }, [props.obj?.expandable, props.arr?.length, props.id, props.onLoadLeafCall]);

    React.useEffect(() => {
        const newCollapsed = isCollapsed(props.collapsed, props.collapsedDict, props.id);

        let loadLeaf = !newCollapsed;

        if (collapsed !== newCollapsed) {
            setCollapsed(newCollapsed);
        }

        if (loadLeaf) {
            tryToLoadLeaf();
        }
    }, [props.collapsed, props.collapsedDict, props.id, tryToLoadLeaf]);

    React.useEffect(() => {
        const newChecked = isChecked(props.checked, props.checkedItems, props.id);

        if (checked !== newChecked) {
            setChecked(newChecked);
        }
    }); // props.checkedItems - can do not change its object but values.

    const handleOnCollapsedChange = React.useCallback(
        (id: string, collapsed: boolean) => {
            if (props.onCollapsedChange != null) {
                props.onCollapsedChange(id, collapsed);
            }
        },
        [props.onCollapsedChange]
    );

    const handleOnCollapsedClick = (e: React.MouseEvent<HTMLElement>) => {
        if (collapsed) {
            tryToLoadLeaf();
        }

        const newCollapsed = !collapsed;

        setCollapsed(newCollapsed);

        if (props.collapsedDict != null) {
            handleOnCollapsedChange(props.id, newCollapsed);
        }

        e.stopPropagation();
        e.preventDefault();
    };

    const handleOnCheckboxChange = (value: boolean) => {
        setChecked(value);

        if (typeof props.onCheckedChange === 'function') {
            props.onCheckedChange(props.id, value);
        }
    };

    const handleOnItemSelect = (e) => {
        if (props.id == null) {
            return;
        }

        if (props.showCheckbox) {
            handleOnCheckboxChange(!checked);
        }

        if (typeof props.onSelect === 'function') {
            props.onSelect(props.id);
        }
    };

    const handleOnItemDoubleClick = (e) => {
        if (props.id == null) {
            return;
        }

        if (typeof props.onDoubleClick === 'function') {
            props.onDoubleClick(props.id);
        }
    };

    let items = props.arr;

    if (typeof props.displayFilter === 'function') {
        items = _.filter(items, props.displayFilter);
    }

    let content: React.JSX.Element;

    if (!collapsed) {
        content = (
            <ul className="nodes-container list-unstyled">
                {_.map(items, (item, i) => {
                    return (
                        <TreeItemGeneral
                            key={i.toString()}
                            obj={item}
                            id={item.id}
                            name={item.name}
                            arr={_.filter(item.children, (objChild) => {
                                if (typeof props.hiddenFilter === 'function') {
                                    return props.hiddenFilter(objChild) !== true;
                                }
                                return true;
                            })}
                            select={props.select}
                            focusedItemID={props.focusedItemID}
                            collapsedDict={props.collapsedDict}
                            checkedItems={props.checkedItems}
                            showCheckbox={props.showCheckbox}
                            getCustomIcon={props.getCustomIcon}
                            nameFunc={props.nameFunc}
                            displayFilter={props.displayFilter}
                            hiddenFilter={props.hiddenFilter}
                            onSelect={props.onSelect}
                            onDoubleClick={props.onDoubleClick}
                            onCollapsedChange={handleOnCollapsedChange}
                            onLoadLeafCall={props.onLoadLeafCall}
                            onCheckedChange={props.onCheckedChange}
                        />
                    );
                })}
            </ul>
        );
    }

    if (props.shadow) {
        return content;
    }

    const item = props.obj;

    let style: React.CSSProperties;

    if (item.backgroundColor != null) {
        style = {
            background: item.backgroundColor
        };
    }

    const expandable = items.length > 0 || (props.obj != null && props.obj.expandable);

    let toggler: React.JSX.Element;

    if (expandable) {
        toggler = (
            <div className="node-toggler">
                <i
                    className={'fa toggle' + (collapsed ? ' fa-caret-right' : ' fa-caret-down')}
                    onClick={handleOnCollapsedClick}
                ></i>
            </div>
        );
    }

    let title: string | React.JSX.Element[] = props.name;

    if (typeof props.nameFunc === 'function') {
        title = props.nameFunc(props.obj, title);
    }

    let checkbox: React.JSX.Element;

    if (props.showCheckbox) {
        checkbox = (
            <div className="node-checkbox">
                <BooleanField title={''} value={checked} onChange={handleOnCheckboxChange} />
            </div>
        );
    }

    let icon: React.JSX.Element;

    if (typeof props.getCustomIcon === 'function') {
        icon = props.getCustomIcon(props.obj);
    } else {
        icon = <span className="folder-icon glyphicon glyphicon-folder-close" onClick={handleOnItemSelect}></span>;
    }

    return (
        <li
            key={props.id}
            className={'node-item' + (props.id === props.select ? ' selected' : '') + (!expandable ? ' leaf' : '')}
            style={style}
        >
            {toggler}

            <span className="node-title">
                {checkbox}

                <div className="node-folder-icon" onClick={handleOnItemSelect}>
                    {icon}
                </div>

                <div className={'node-text' + (props.id === props.focusedItemID ? ' focused' : '')}>
                    <span onClick={handleOnItemSelect} onDoubleClick={handleOnItemDoubleClick}>
                        {title}
                    </span>
                </div>
            </span>

            {content}
        </li>
    );
}
